import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { IContact } from '../../../extViewmodels/sdk/models';
import type { CallPipelineApi } from '../call/useCallPipeline';
import type { VoiceAgentTranscriptBridge } from '../transcript/voiceAgentSource';
import { buildSystemPrompt } from './promptService';
import type { RepCoachingGoals, Scenario } from './types';
import { useStartElevenLabsPracticeCall } from './usePracticeCallMutations';
import { VoiceAgent } from './VoiceAgent';

/**
 * Lightweight lead-contact shape accepted by {@link useSparSession.start}. Only
 * the fields the prompt builder + practice-session peer label need are required;
 * the focused-view lead contact is a superset of this.
 */
export type SparStartContact = {
	id: string;
	firstName?: string;
	lastName?: string;
	name?: string;
	title?: string;
};

export type UseSparSessionArgs = {
	companyId: string | undefined;
	leadServedId?: string;
	/** Practice FSM controls from `useCallPipeline`. */
	pipeline: Pick<CallPipelineApi, 'startPractice' | 'endPractice'>;
	/** Fan-out bridge wired into the `VoiceAgent`'s `onNewTranscriptEntry`. */
	transcriptBridge: VoiceAgentTranscriptBridge;
	/** From `useCoaching`; invoked on `end()` to kick off the post-call summary. */
	requestPostCallSummary: () => void;
};

export type SparStartOpts = {
	contact: SparStartContact;
	scenario: Scenario | null;
};

export type UseSparSessionApi = {
	/** Start an ElevenLabs practice session: mint tokens, build the prompt, connect, enter the practice FSM state. */
	start(opts: SparStartOpts): Promise<void>;
	/** End the active practice session: disconnect the agent, request a summary, leave the practice FSM state. */
	end(): void;
	/** True while `start()` is in flight (tokens minting / agent connecting). */
	isConnecting: boolean;
	/** True once the agent is connected and the FSM is in the practice state. */
	isActive: boolean;
	/** Last connection error message, or null. */
	error: string | null;
};

/**
 * v1 default rep coaching goals. The legacy `VoiceAgentModal` sourced these from
 * `useRepCoachingGoals(userSession.user.id)`; the forked focused-view flow uses a
 * fixed default for the first cut.
 *
 * TODO(focused-view): fork/replace with a real per-rep goals source (the legacy
 * `useRepCoachingGoals` hook) so practice difficulty + focus reflect the rep.
 */
const defaultRepGoals: RepCoachingGoals = { workingOn: '', tysonMaxDifficulty: 3 };

/** Resolve the human-readable peer name for the practice strip from the contact. */
function resolvePeer(contact: SparStartContact): string {
	if (contact.name) return contact.name;
	const joined = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
	return joined || 'Practice prospect';
}

/** Best-effort stable session id when the agent + server provide none. */
function fallbackSessionId(): string {
	return globalThis.crypto?.randomUUID?.() ?? 'spar';
}

/**
 * Orchestrates an AI voice-roleplay ("Spar") practice session for the focused
 * view. Replicates the legacy `VoiceAgentModal.handleConnect` ElevenLabs path
 * against the forked `spar/` primitives, but is self-contained: it owns the `VoiceAgent`
 * instance, drives the call pipeline's practice FSM state, and feeds transcript
 * lines into the shared coaching pipeline via the supplied bridge.
 *
 * Coaching lifecycle is intentionally NOT managed here — the focused view keeps a
 * single long-lived coaching conversation per lead; this hook only triggers the
 * post-call summary on `end()`.
 */
export function useSparSession(args: UseSparSessionArgs): UseSparSessionApi {
	const { companyId, leadServedId, pipeline, transcriptBridge, requestPostCallSummary } = args;
	const startMutation = useStartElevenLabsPracticeCall();

	const agentRef = useRef<VoiceAgent | null>(null);
	const [isConnecting, setIsConnecting] = useState(false);
	const [isActive, setIsActive] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Refs mirror the connecting/active state so the async `start` and the
	// (re-)entrant `end` can read the latest value synchronously without the
	// stale-closure problem a state read inside `useCallback` would have. Always
	// update the ref alongside its state setter so the two never diverge.
	const isConnectingRef = useRef(false);
	const isActiveRef = useRef(false);
	// Tracks whether the host is still mounted. Read after the `connect()` await so
	// a mid-connect unmount can tear down the freshly-connected agent instead of
	// entering the practice FSM state against a torn-down React tree.
	const isMountedRef = useRef(true);
	const setConnecting = useCallback((value: boolean): void => {
		isConnectingRef.current = value;
		setIsConnecting(value);
	}, []);
	const setActive = useCallback((value: boolean): void => {
		isActiveRef.current = value;
		setIsActive(value);
	}, []);

	const start = useCallback(
		async ({ contact, scenario }: SparStartOpts): Promise<void> => {
			// Guard: a practice call cannot be minted without a company, and a
			// session that's already connecting or active must not be double-started
			// — a second `VoiceAgent` would overwrite `agentRef.current` and orphan
			// the first live connection.
			if (!companyId || isConnectingRef.current || isActiveRef.current) return;

			setError(null);
			setConnecting(true);

			// Tracks whether the agent was constructed so the catch block knows
			// whether there's anything to disconnect.
			let agent: VoiceAgent | null = null;
			try {
				// Step 1: mint tokens + lead context from the ElevenLabs practice endpoint.
				// The scenario is applied entirely client-side (its prompt is injected into
				// the ElevenLabs agent below), so no scenario identifier is sent to the backend.
				const response = await startMutation.mutateAsync({
					companyId,
					leadServedId: leadServedId ?? '',
					contactId: contact.id,
				});

				// Step 2: build the sparring-partner system prompt. `buildSystemPrompt`
				// reads `firstName`/`lastName` (via getDisplayName) and `jobTitle`; map
				// the lightweight contact shape onto the IContact fields it consumes.
				const promptContact: IContact = {
					firstName: contact.firstName,
					lastName: contact.lastName,
					jobTitle: contact.title,
				} as IContact;
				const systemPrompt = buildSystemPrompt(
					promptContact,
					scenario?.prompt ?? '',
					'cold-call',
					response.leadAIContext,
					defaultRepGoals
				);

				// Step 3: construct the agent, wiring transcript lines into the bridge.
				// The ElevenLabs token is the server-minted `externalConversationToken`
				// (a `wss://` signed URL or a conversation token), falling back to
				// `clientToken` — mirroring the legacy `VoiceAgentModal`. Assign to the
				// ref BEFORE the connect await so an unmount mid-connect can find and
				// disconnect it — otherwise a live mic/socket session would be orphaned
				// when connect later resolves.
				agent = new VoiceAgent({
					apiKey: response.externalConversationToken ?? response.clientToken,
					onNewTranscriptEntry: transcriptBridge.push,
				});
				agentRef.current = agent;

				// Step 4: connect (ElevenLabs Conversational AI).
				await agent.connect('cold-call', systemPrompt);

				// If the host unmounted while connect was in flight, the unmount-cleanup
				// already ran (and saw a non-null ref to disconnect, OR will not have, if
				// timing was tight). Either way, tear down the now-live agent and bail
				// without entering the practice FSM state / flipping active — there's no
				// UI left to drive it.
				if (!isMountedRef.current) {
					agent.disconnect();
					agentRef.current = null;
					setConnecting(false);
					return;
				}

				// Step 5: enter the practice FSM state. Prefer the live conversation id
				// for the session sid, falling back to the server phoneCallId, then a
				// generated id so the strip always has a stable key.
				const conversationId = agent.getConversationId() ?? undefined;
				pipeline.startPractice({
					sid: conversationId ?? response.phoneCallId ?? fallbackSessionId(),
					phoneCallId: response.phoneCallId,
					conversationId,
					peer: resolvePeer(contact),
					startedAt: Date.now(),
					scenarioId: scenario?.id,
				});

				setActive(true);
				setConnecting(false);
			} catch (err) {
				// Clean up a partially-constructed session and surface the failure.
				// Reset the connecting ref/state so a retry isn't blocked by the guard.
				agent?.disconnect();
				agentRef.current = null;
				const message = err instanceof Error ? err.message : 'Failed to start practice session';
				setError(message);
				setConnecting(false);
				setActive(false);
				toast.error(message);
			}
		},
		[companyId, leadServedId, pipeline, transcriptBridge, startMutation, setConnecting, setActive]
	);

	const end = useCallback((): void => {
		// Idempotent: redundant calls (e.g. the End button AND the auto-reclaim
		// effect in FocusedLeadView both firing) no-op once the session is torn down.
		if (!isActiveRef.current) return;
		agentRef.current?.disconnect();
		agentRef.current = null;
		requestPostCallSummary();
		pipeline.endPractice();
		setActive(false);
	}, [pipeline, requestPostCallSummary, setActive]);

	// Unmount-only safety net: if the host (FocusedLeadView) unmounts mid-session
	// — e.g. an exit path that didn't route through `end()` — a live VoiceAgent
	// would otherwise keep the hot mic + ElevenLabs Conversation session open
	// with no UI to stop it. Disconnecting via the ref frees those resources
	// without depending on `end`'s identity (so this effect runs exactly once,
	// on unmount). Deliberately does NOT call `requestPostCallSummary` /
	// `pipeline.endPractice`: those touch React/query state that is tearing down
	// here, and the graceful teardown (summary + endPractice) belongs in `end()`,
	// which the gated exit handlers now invoke before unmount.
	useEffect(
		() => () => {
			// Flag first so an in-flight `start()` (awaiting connect) bails after the
			// await instead of entering the practice state against a torn-down tree.
			isMountedRef.current = false;
			agentRef.current?.disconnect();
			agentRef.current = null;
		},
		[]
	);

	return { start, end, isConnecting, isActive, error };
}
